import { ModelKey, ViewKey } from '@view-model/domain/key';
import type { ModelId, ViewId, ViewJSON, ViewType } from 'balus-common';
import { ViewName, ViewSetting } from '@view-model/domain/view';
import { Model } from '@framework/domain';
import { Point, Size } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import { Timestamp } from '@framework/Timestamp';
import { isThemeColor, ThemeColor } from '../../models/common/color';

interface IView {
  key: ViewKey;
  modelKey: ModelKey;
  name: ViewName;
  size: Size;
  setting: ViewSetting;
  order: number;
  createdAt: Timestamp;
  themeColor: ThemeColor;
  viewType: ViewType;
  meaningPanelViewId: ViewId | null;
  meaningPanelIsOpen: boolean | null;
}

type ViewCreateParameters = {
  modelKey: ModelKey;
  name: ViewName;
  size: Size;
  setting: ViewSetting;
  order?: number;
  createdAt?: Timestamp;
  viewType?: ViewType;
  meaningPanelViewId?: ViewId | null;
  meaningPanelIsOpen?: boolean | null;
};

export class ViewEntity extends Model<ViewKey> {
  public readonly key: ViewKey;
  public readonly modelKey: ModelKey;
  public readonly themeColor: ThemeColor;
  private readonly _name: ViewName;
  private readonly _size: Size;
  private readonly _setting: ViewSetting;
  private readonly _order: number;
  private readonly _createdAt: Timestamp;
  private readonly _viewType: ViewType;
  private readonly _meaningPanelViewId: ViewId | null;
  private readonly _meaningPanelIsOpen: boolean | null;

  public constructor(attributes: IView) {
    super();
    this.key = attributes.key;
    this.modelKey = attributes.modelKey;
    this.themeColor = attributes.themeColor;
    this._name = attributes.name;
    this._size = attributes.size;
    this._setting = attributes.setting;
    this._order = attributes.order;
    this._createdAt = attributes.createdAt;
    this._viewType = attributes.viewType || 'original';
    this._meaningPanelViewId = attributes.meaningPanelViewId || null;
    this._meaningPanelIsOpen = attributes.meaningPanelIsOpen || null;
  }

  public static buildNew(attributes: ViewCreateParameters): ViewEntity {
    return new ViewEntity({
      key: ViewKey.buildNew(),
      modelKey: attributes.modelKey,
      themeColor: ThemeColor.White,
      name: attributes.name,
      size: attributes.size,
      setting: attributes.setting,
      order: attributes.order || Timestamp.now().toUnixTimestamp(),
      createdAt: attributes.createdAt || Timestamp.now(),
      viewType: attributes.viewType || 'original',
      meaningPanelViewId: attributes.meaningPanelViewId || null,
      meaningPanelIsOpen: attributes.meaningPanelIsOpen || null,
    });
  }

  public dump(): ViewJSON {
    return {
      key: this.key.toString(),
      modelKey: this.modelKey.toString(),
      name: this.name.value,
      size: this.size.dump(),
      setting: this.setting.dump(),
      order: this.order,
      createdAt: this.createdAt.toUnixTimestamp(),
      themeColor: this.themeColor,
      viewType: this.viewType,
      meaningPanelViewId: this.meaningPanelViewId,
      meaningPanelIsOpen: this.meaningPanelIsOpen,
    };
  }

  public dumpWithUnlock(): ViewJSON {
    return {
      key: this.key.toString(),
      modelKey: this.modelKey.toString(),
      name: this.name.value,
      size: this.size.dump(),
      setting: this.setting.dumpWithUnlock(),
      order: this.order,
      createdAt: this.createdAt.toUnixTimestamp(),
      themeColor: this.themeColor,
      viewType: this.viewType,
      meaningPanelViewId: this.meaningPanelViewId,
      meaningPanelIsOpen: this.meaningPanelIsOpen,
    };
  }

  public static load(j: ViewJSON): ViewEntity {
    return new ViewEntity({
      key: new ViewKey(j.key),
      modelKey: new ModelKey(j.modelKey),
      name: ViewName.load(j.name),
      size: Size.load(j.size),
      setting: j.setting ? ViewSetting.load(j.setting) : ViewSetting.buildNew(),
      order: j.order,
      createdAt: new Timestamp(j.createdAt),
      themeColor: isThemeColor(j.themeColor) ? j.themeColor : ThemeColor.White,
      viewType: j.viewType,
      meaningPanelViewId: j.meaningPanelViewId,
      meaningPanelIsOpen: j.meaningPanelIsOpen,
    });
  }

  get id(): ViewId {
    return this.key.id as ViewId;
  }

  get modelId(): ModelId {
    return this.modelKey.modelID;
  }

  public clone(): ViewEntity {
    return ViewEntity.load(this.dump());
  }

  public cloneWithNewMeaningPanelViewId(meaningPanelViewId: ViewId): ViewEntity {
    return ViewEntity.load({
      ...this.dump(),
      meaningPanelViewId,
    });
  }

  public cloneWithNewMeaningPanelIsOpen(meaningPanelIsOpen: boolean): ViewEntity {
    return ViewEntity.load({
      ...this.dump(),
      meaningPanelIsOpen,
    });
  }

  public cloneNew(modelKeyMap: Record<string, ModelKey>, meaningPanelIsOpen?: boolean): ViewEntity {
    return ViewEntity.load(
      Object.assign(this.dump(), {
        key: ViewKey.buildNew().toString(),
        modelKey: modelKeyMap[this.modelKey.toString()].toString(),
        createdAt: Timestamp.now().toISOString(),
        meaningPanelViewId: null,
        meaningPanelIsOpen: meaningPanelIsOpen ?? null,
      })
    );
  }

  public cloneNewWithMeaningPanelViewId(modelKeyMap: Record<string, ModelKey>, meaningPanelViewId: ViewId): ViewEntity {
    return ViewEntity.load(
      Object.assign(this.dump(), {
        key: ViewKey.buildNew().toString(),
        modelKey: modelKeyMap[this.modelKey.toString()].toString(),
        createdAt: Timestamp.now().toISOString(),
        meaningPanelViewId,
        meaningPanelIsOpen: null,
      })
    );
  }

  public isEqual(other: ViewEntity): boolean {
    return (
      other instanceof ViewEntity &&
      this.key.isEqual(other.key) &&
      this.name.isEquals(other.name) &&
      this.size.isEqual(other.size) &&
      this.setting.isEquals(other.setting) &&
      this.createdAt.isEqual(other.createdAt) &&
      this.themeColor === other.themeColor &&
      this.viewType === other.viewType &&
      this.meaningPanelViewId === other.meaningPanelViewId &&
      this.meaningPanelIsOpen === other.meaningPanelIsOpen
    );
  }

  public get name(): ViewName {
    return this._name;
  }

  public get size(): Size {
    return this._size;
  }

  public get setting(): ViewSetting {
    return this._setting;
  }

  public get order(): number {
    return this._order;
  }

  public get createdAt(): Timestamp {
    return this._createdAt;
  }

  public get viewType(): ViewType {
    return this._viewType;
  }

  public get meaningPanelViewId(): ViewId | null {
    return this._meaningPanelViewId;
  }

  public get meaningPanelIsOpen(): boolean | null {
    return this._meaningPanelIsOpen;
  }

  public getRect(position: { x: number; y: number }): Rect {
    const point = new Point(position.x, position.y);
    return new Rect(point, this._size);
  }

  intersectsWithRect(position: { x: number; y: number }, rect: Rect): boolean {
    return this.getRect(position).intersects(rect);
  }

  withName(name: ViewName): ViewEntity {
    return ViewEntity.load({ ...this.dump(), name: name.value });
  }

  withSize(size: Size): ViewEntity {
    return ViewEntity.load({ ...this.dump(), size: size.dump() });
  }

  withOrder(order: number): ViewEntity {
    return ViewEntity.load({ ...this.dump(), order });
  }

  private _comparisonValue(): string {
    const order = this.order;
    const createdAt = this.createdAt;
    return `${createdAt.valueOf()} ${order} `;
  }

  public compareTo(other: ViewEntity): number {
    const myComparison = this._comparisonValue();
    const otherComparison = other._comparisonValue();

    if (myComparison > otherComparison) return +1;
    if (myComparison < otherComparison) return -1;
    return 0;
  }
}
